import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams, useParams } from 'react-router-dom'
import { get, onValue, ref, update } from 'firebase/database'

import { db } from '../lib/firebase.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'
import { setTvScrollSpeed, setTvNetMode } from '../lib/data.js'
import { netAvailableFor } from '../lib/scoring.js'
import CLUB from '../config/club.js'
import TvLeaderboard from './TvLeaderboard.jsx'
import LeaderboardApp from './LeaderboardApp.jsx'
import TvBracketGrid from './TvBracketGrid.jsx'
import TvCourseScrollMap from './TvCourseScrollMap.jsx'
import PhotoWallApp from '../photos/PhotoWallApp.jsx'
import TvStage from './TvStage.jsx'
import { TV_ROTATE_ORDER, CALCUTTA_ROTATE_ORDER, normalizeTvMode, isCalcuttaScreen } from './tvScenes.js'
import CalcuttaLive from '../calcutta/CalcuttaLive.jsx'

// ── Active-tournament auto-pick ───────────────────────────────────────────
//
// Used by every /tv* URL to pin the screen to the current open event without
// requiring a hand-typed ?t=ID. The hint comes from a ?t= URL param when
// present (or from a parent prop, for the admin monitor-wall tab); otherwise
// it picks the most recent open non-demo tournament from the live snapshot.
function useActiveTournamentId(propHint = null) {
  const [params] = useSearchParams()
  const urlHint = params.get('t')
  const hint = propHint ?? urlHint
  const [allTournaments, setAllTournaments] = useState({})

  useEffect(() => {
    if (hint) return
    const off = onValue(ref(db, 'tournaments'), (snap) => setAllTournaments(snap.val() || {}))
    return () => off()
  }, [hint])

  return useMemo(() => {
    if (hint) return hint
    // TEST: deterministic, flights-preferring pick — same as the /1,/2,/3
    // screen path — so /tv/{kind} channels never latch onto an empty qualifying
    // event (and demos are eligible, since the test data is demo-based).
    if (PBCC_PREMIUM) return pickPremiumCurrentEventId(allTournaments)
    const entries = Object.entries(allTournaments).filter(
      ([, v]) => !v?.info?.isDemo && v?.info?.status !== 'closed',
    )
    if (entries.length === 0) return null
    entries.sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
    return entries[0][0]
  }, [hint, allTournaments])
}

// ── TV Mode ────────────────────────────────────────────────────────────────
// The full-screen TV board routes (/tv/*, /tv?screen=N, /1/2/3) add `pbcc-tv-mode`
// to <html> for the run of the page. That class (see styles.css) applies a
// TV-tuned visual pass — darker green, heavier text, shadows, higher contrast —
// so the boards don't wash out on real TV panels. It is TEST-only and is NOT
// added by the embedded admin monitor tiles, so the admin/desktop theme is
// untouched. Removed on unmount so navigating back to the admin restores it.
function useTvModeClass() {
  useEffect(() => {
    if (!PBCC_PREMIUM) return undefined
    const el = document.documentElement
    el.classList.add('pbcc-tv-mode')
    return () => el.classList.remove('pbcc-tv-mode')
  }, [])
}

// ── Scene → board mapping (the SINGLE place a scene key becomes a board) ────
// Reused by BOTH the fixed /tv/{kind} channels AND the remote-controlled
// /tv?screen=N. Every board is the exact same component the TV Monitor tiles
// use, so the remote and the "open a URL" fallback render identically.
export function SceneView({ kind, tournamentId }) {
  const board = (() => {
    switch (kind) {
      case 'scores':     return <TvLeaderboard tournamentIdProp={tournamentId} />
      case 'matchboard': return <LeaderboardApp tournamentIdProp={tournamentId} tv />
      case 'brackets':   return <TvBracketGrid tournamentIdProp={tournamentId} />
      case 'scrollmap':  return <TvCourseScrollMap tournamentIdProp={tournamentId} />
      case 'photos':     return <PhotoWallApp tournamentIdProp={tournamentId} />
      case 'split':      return <SplitScreen tournamentIdProp={tournamentId} />
      // Legacy fixed route: /tv/map + /map = production map, no scroll.
      // legacy 'map' key → the ONE scrolling map (full-screen twin retired)
      case 'map':        return <TvCourseScrollMap tournamentIdProp={tournamentId} />
      default:
        // Calcutta screens (from the Calcutta tab's screen control) — same TV pool.
        if (typeof kind === 'string' && kind.startsWith('calcutta-')) {
          return <CalcuttaLive tournamentIdProp={tournamentId} screenProp={kind.slice('calcutta-'.length)} />
        }
        return null
    }
  })()
  // TEST full-screen TV Mode: fit-to-viewport so the board FILLS the TV at 100%
  // browser zoom (no more manually zooming the TV browser to ~60%). These fluid
  // boards are laid out on TvStage's fixed 1920×1080 canvas and uniformly scaled
  // to the actual viewport — the canvas's transform makes it the containing block
  // for their position:fixed roots, so they fill it without any rewrite. Brackets
  // and the scroll map ALREADY self-scale (their own internal TvStage), so they're
  // left untouched here to avoid double-scaling. Club/live are unchanged.
  // NOTE: only the fixed-PIXEL boards (leaderboard/matchboard) belong on
  // TvStage's scaled 1920×1080 canvas. The photo wall + split are FLUID and
  // full-bleed (min-h-screen + object-cover); wrapping them in TvStage left
  // their 100vh height shorter than the 1080px canvas, exposing the stage's
  // #081E12 background as a teal band and boxing portraits. Keep them off it.
  const fit = kind === 'scores' || kind === 'matchboard'
  if (PBCC_PREMIUM && board && fit) {
    return <TvStage background="#081E12">{board}</TvStage>
  }
  return board
}

// ── /tv/scores | /tv/brackets | /tv/matchboard | /tv/scrollmap | /tv/photos |
// /tv/split | /tv/map ──────────────────────────────────────────────────────
// Fixed single-channel TV view pinned to the auto-picked (non-demo) tournament
// (or an explicit ?t=). `kind` selects which board.
export function TvScreen({ kind }) {
  useTvModeClass()
  const tournamentId = useActiveTournamentId()
  return <SceneView kind={kind} tournamentId={tournamentId} />
}

// ── Split screen — leaderboard (≈2/3, left) + photo wall (≈1/3, right) ─────
// One screen, both channels live. Reuses TvLeaderboard (fill mode: scrolling +
// full chrome, sized to its pane) and PhotoWallApp (embedded: fills + auto-
// advances) — neither is rebuilt. Full-screen / no chrome when standalone;
// `embedded` makes it fit a parent box (the admin monitor-wall tile).
export function SplitScreen({ tournamentIdProp = null, embedded = false }) {
  return (
    <div
      className={embedded ? 'w-full h-full flex' : 'fixed inset-0 flex'}
      style={{ backgroundColor: '#000', ...(embedded ? {} : { zIndex: 1 }) }}>
      <div className="relative overflow-hidden" style={{ width: '66.6667%', height: '100%' }}>
        <TvLeaderboard tournamentIdProp={tournamentIdProp} fill />
      </div>
      <div
        className="relative overflow-hidden"
        style={{ width: '33.3333%', height: '100%', borderLeft: '2px solid #2E4A3A' }}>
        <PhotoWallApp tournamentIdProp={tournamentIdProp} embedded />
      </div>
    </div>
  )
}

// ── /tv (rotator) ─────────────────────────────────────────────────────────
//
// Cycles through scores → map → photos every ROTATE_MS. Each child is mounted
// only while it's the active panel, so listeners and animation timers don't
// run in the background. A small "Now showing" pill in the corner shows the
// current channel and the next one queued up.
const ROTATE_MS = 17000
const PANELS = [
  { key: 'scores', label: 'Match-Play Leaderboard', icon: '🏆' },
  { key: 'map',    label: 'Course Map',             icon: '📡' },
  { key: 'photos', label: 'Photo Wall',             icon: '📸' },
]

// Dispatcher for the /tv route. With ?screen=N it becomes a remote-controlled
// TV (ControlledTvScreen); with no screen param it's the original auto-rotator
// (PlainTvRotator), whose behavior is unchanged.
export function TvRotator() {
  const [params] = useSearchParams()
  const screen = (params.get('screen') || '').trim()
  return screen ? <ControlledTvScreen screenId={screen} /> : <PlainTvRotator />
}

// Normalize a short /tv/{seg} path segment to a canonical screen id: digits with
// an optional trailing "C" (uppercased) so a Calcutta short URL like /tv/1c
// joins the SAME "C" pool as /tv?screen=1C and the Calcutta screen control
// (tvControl/screens keys are case-sensitive). Returns null for anything else.
function normalizeScreenSeg(seg) {
  const m = String(seg || '').trim().match(/^(\d+)([cC])?$/)
  return m ? m[1] + (m[2] ? 'C' : '') : null
}

// ── /tv/1, /tv/2 … (tournament) and /tv/1c, /tv/2c … (Calcutta "C" pool) ─────
// Short path alias for /tv?screen=N — renders the exact same remote-controlled
// screen. An unrecognized segment falls through to the plain rotator (the fixed
// /tv/{kind} channels win via React Router's static-over-dynamic ranking).
export function TvScreenByPath() {
  const { screen } = useParams()
  const id = normalizeScreenSeg(screen)
  return id ? <ControlledTvScreen screenId={id} /> : <PlainTvRotator />
}

// ── /tv?screen=N — per-TV remote-controlled screen ────────────────────────
// Registers itself at tvControl/screens/{N} (heartbeat every 30s) and renders
// whichever scene the /tvcontrol panel set — now ALL SIX real channels
// (scores, brackets, matchboard, scrollmap, photos, split) plus Rotate, the
// exact same boards the TV Monitor tab exposes. The tournament is pinned by
// the panel (cfg.tournamentId) or defaults to the current active event
// INCLUDING demo/practice, so a demo TV actually shows content. A brand-new
// screen with no setting defaults to Rotate @ 30s. Full-screen, no chrome.
const HEARTBEAT_MS = 30000

// Default tournament for a controlled TV: the current active event, PREFERRING
// a real (non-demo) tournament but FALLING BACK to the most-recent demo/practice
// (same rule as /live) so a demo-only club still lights up the TVs. The fixed
// /tv/{kind} channels keep their non-demo auto-pick via useActiveTournamentId.
function useDefaultTvTournamentId() {
  const [all, setAll] = useState({})
  useEffect(() => {
    const off = onValue(ref(db, 'tournaments'), (snap) => setAll(snap.val() || {}))
    return () => off()
  }, [])
  return useMemo(() => {
    const open = Object.entries(all).filter(([, v]) => v?.info?.status !== 'closed')

    // TEST: deterministic, flights-preferring pick (no oscillation).
    if (PBCC_PREMIUM) return pickPremiumCurrentEventId(all)

    // Club/live: original behavior, untouched.
    const real = open
      .filter(([, v]) => !v?.info?.isDemo)
      .sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
    if (real[0]) return real[0][0]
    const demo = open
      .filter(([, v]) => !!v?.info?.isDemo)
      .sort((a, b) => ((b[1]?.info?.demoCreatedAt || b[1]?.info?.createdAt || 0) -
                       (a[1]?.info?.demoCreatedAt || a[1]?.info?.createdAt || 0)))
    return demo[0]?.[0] || null
  }, [all])
}

function ControlledTvScreen({ screenId }) {
  useTvModeClass()
  const [cfg, setCfg] = useState(null)
  const [idx, setIdx] = useState(0)
  const autoId = useDefaultTvTournamentId()

  // Register + heartbeat. Seeds defaults (rotate @ 30s) WITHOUT clobbering any
  // mode/speed/tournament the control panel already wrote.
  useEffect(() => {
    const path = `tvControl/screens/${screenId}`
    const stamp = () => update(ref(db, path), { lastSeen: Date.now() }).catch(() => {})
    get(ref(db, path))
      .then((snap) => {
        const cur = snap.val() || {}
        const patch = { lastSeen: Date.now() }
        // Calcutta "C" TVs open on the title card, not the tournament rotate.
        if (cur.mode == null) patch.mode = isCalcuttaScreen(screenId) ? 'calcutta-title' : 'rotate'
        if (cur.rotateSeconds == null) patch.rotateSeconds = 30
        return update(ref(db, path), patch)
      })
      .catch(() => stamp())
    const id = setInterval(stamp, HEARTBEAT_MS)
    return () => clearInterval(id)
  }, [screenId])

  // Live config listener — flips the TV within ~1s of a panel change.
  useEffect(() => {
    const off = onValue(ref(db, `tvControl/screens/${screenId}`), (snap) => setCfg(snap.val() || {}))
    return () => off()
  }, [screenId])

  // Global "All Screens" announcement listener.
  const [globalAnn, setGlobalAnn] = useState(null)
  useEffect(() => {
    const off = onValue(ref(db, 'tvControl/announcement'), (snap) => setGlobalAnn(snap.val() || null))
    return () => off()
  }, [])

  const mode = normalizeTvMode(cfg?.mode)
  // Calcutta "C" TVs rotate through Calcutta screens; tournament TVs use the normal order.
  const rotateOrder = isCalcuttaScreen(screenId) ? CALCUTTA_ROTATE_ORDER : TV_ROTATE_ORDER
  const rotateSeconds = Number(cfg?.rotateSeconds) > 0 ? Number(cfg.rotateSeconds) : 30
  // Panel-pinned tournament wins; otherwise the demo-inclusive current event.
  const tournamentId = cfg?.tournamentId || autoId
  // Per-screen announcement wins over the global "All Screens" one.
  const announceText = String(cfg?.announcement?.text || globalAnn?.text || '').trim()

  // Rotation timer — only while in rotate mode; resets when the speed changes.
  useEffect(() => {
    if (mode !== 'rotate') return undefined
    const id = setInterval(() => setIdx((n) => (n + 1) % rotateOrder.length), rotateSeconds * 1000)
    return () => clearInterval(id)
  }, [mode, rotateSeconds])

  const active = mode === 'rotate' ? rotateOrder[idx % rotateOrder.length] : mode

  return (
    <div className="relative" style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      <SceneView kind={active} tournamentId={tournamentId} />
      {announceText ? <AnnouncementOverlay text={announceText} /> : null}
    </div>
  )
}

// Scrolling announcement overlaid on top of the live scene — a translucent gold
// banner that rises up the screen and repeats, so the leaderboard / content
// behind it stays visible. Purely presentational; never blocks touches.
function AnnouncementOverlay({ text }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483000, pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, animation: 'tvann-up 16s linear infinite', willChange: 'transform' }}>
        <div style={{
          margin: '0 4vw', padding: '2.4vh 3vw', borderRadius: 24,
          background: '#123D26',
          border: `3px solid ${CLUB.accentColor || '#FFFFFF'}`,
          color: '#FFE9B0', fontWeight: 900,
          fontSize: 'clamp(30px, 4.6vw, 76px)', lineHeight: 1.15, textAlign: 'center',
          whiteSpace: 'pre-wrap',
        }}>
          <img src={CLUB.logoLight} alt="" style={{ height: 'clamp(72px, 10vw, 168px)', width: 'auto', display: 'block', margin: '0 auto 1.4vh' }} />
          {text}
        </div>
      </div>
      <style>{`@keyframes tvann-up { from { transform: translateY(0); } to { transform: translateY(-100vh); } }`}</style>
    </div>
  )
}

// Original auto-rotator for plain /tv (no screen param). Behavior unchanged.
function PlainTvRotator() {
  useTvModeClass()
  const tournamentId = useActiveTournamentId()
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setIdx((n) => (n + 1) % PANELS.length), ROTATE_MS)
    return () => clearInterval(id)
  }, [])

  const cur = PANELS[idx]
  const next = PANELS[(idx + 1) % PANELS.length]

  return (
    <div className="relative" style={{ backgroundColor: '#000' }}>
      {cur.key === 'scores' && <TvLeaderboard tournamentIdProp={tournamentId} />}
      {cur.key === 'map' && <TvCourseScrollMap tournamentIdProp={tournamentId} />}
      {cur.key === 'photos' && <PhotoWallApp tournamentIdProp={tournamentId} />}
      <div
        className="fixed bottom-4 right-4 px-4 py-2 rounded-xl text-sm font-bold shadow-lg pointer-events-none"
        style={{
          backgroundColor: 'rgba(0,0,0,0.65)',
          color: CLUB.accentColor,
          border: `1px solid ${CLUB.accentColor}55`,
          zIndex: 50,
        }}>
        <span style={{ color: '#FFFFFF' }}>{cur.icon} {cur.label}</span>
        <span className="text-white/50 ml-3 text-sm">Next: {next.icon} {next.label}</span>
      </div>
    </div>
  )
}

// ── Admin monitor-wall tab ────────────────────────────────────────────────
//
// Inside /admin/t/{id} this renders all three TV channels at once in a small
// preview grid. Each pane is the live view bound to the current tournament,
// so admins can see every TV at a glance and click through to open any one
// in a new tab.
export function TvMonitorWall({ tournamentId }) {
  // Live-listen for the current scroll-speed setting so the toggle group
  // reflects whatever's in Firebase even if another admin changes it.
  const [scrollSpeed, setScrollSpeedState] = useState('slow')
  useEffect(() => {
    if (!tournamentId) return
    const off = onValue(
      ref(db, `tournaments/${tournamentId}/tvSettings/scrollSpeed`),
      (snap) => setScrollSpeedState(snap.val() || 'slow'),
    )
    return () => off()
  }, [tournamentId])

  const handleSpeedChange = (next) => {
    setScrollSpeedState(next) // optimistic
    setTvScrollSpeed(tournamentId, next).catch((e) => console.warn('[TV] speed save failed', e))
  }

  // Persisted GROSS/NET display mode (admin control → flows to every TV board +
  // the public leaderboard). Only offered once net data exists.
  const [netMode, setNetModeState] = useState(false)
  const [netAvailable, setNetAvailable] = useState(false)
  useEffect(() => {
    if (!tournamentId) return undefined
    const off1 = onValue(ref(db, `tournaments/${tournamentId}/tvSettings/netMode`), (s) => setNetModeState(!!s.val()))
    const off2 = onValue(ref(db, `tournaments/${tournamentId}`), (s) => setNetAvailable(netAvailableFor(s.val())))
    return () => { off1(); off2() }
  }, [tournamentId])
  const handleNetChange = (on) => {
    setNetModeState(on) // optimistic
    setTvNetMode(tournamentId, on).catch((e) => console.warn('[TV] net save failed', e))
  }

  const tabBase = `/tv`
  const tParam = `?t=${encodeURIComponent(tournamentId)}`
  const tiles = [
    { key: 'scores', label: '🏆 Live Scores', href: `${tabBase}/scores${tParam}`,
      body: <TvLeaderboard tournamentIdProp={tournamentId} embedded /> },
    { key: 'brackets', label: '🏆 Flight Brackets', href: `${tabBase}/brackets${tParam}`,
      body: <TvBracketGrid tournamentIdProp={tournamentId} embedded /> },
    { key: 'matchboard', label: '🏆 Live Leaderboard', href: `${tabBase}/matchboard${tParam}`,
      body: <LeaderboardApp tournamentIdProp={tournamentId} tv embedded /> },
    { key: 'scrollmap', label: '🗺️ Course Scroll Map', href: `${tabBase}/scrollmap${tParam}`,
      body: <TvCourseScrollMap tournamentIdProp={tournamentId} embedded /> },
    { key: 'photos', label: '📸 Photo Wall', href: `${tabBase}/photos${tParam}`,
      body: <PhotoWallApp tournamentIdProp={tournamentId} embedded /> },
    { key: 'split', label: '🔀 Split (Scores + Photos)', href: `${tabBase}/split${tParam}`,
      body: <SplitScreen tournamentIdProp={tournamentId} embedded /> },
  ]
  return (
    <div>
      <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-gray-600 flex-1 min-w-[280px]">
          Live preview of every TV. Click any tile to open that channel in a new tab —
          ideal for setting each clubhouse TV to its own URL.
        </p>
        <div className="flex items-center gap-3">
          {netAvailable ? <NetPicker value={netMode} onChange={handleNetChange} /> : null}
          <SpeedPicker value={scrollSpeed} onChange={handleSpeedChange} />
          <Link
            to={`/tv${tParam}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg px-3 py-1.5 text-sm font-extrabold uppercase tracking-wider whitespace-nowrap"
            style={{ backgroundColor: CLUB.primaryColor, color: '#FFFFFF' }}>
            ↗ Open Rotator
          </Link>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {tiles.map((t) => (
          <a
            key={t.key}
            href={t.href}
            target="_blank"
            rel="noreferrer"
            className="block rounded-2xl overflow-hidden bg-black border border-gray-200 shadow hover:shadow-xl transition"
            style={{ height: '46vh', minHeight: 320 }}>
            <div
              className="px-3 py-2 text-sm font-extrabold uppercase tracking-wider flex items-center justify-between"
              style={{ backgroundColor: CLUB.primaryColor, color: CLUB.accentColor }}>
              <span>{t.label}</span>
              <span className="text-white/70 text-sm">↗ Open</span>
            </div>
            <div className="relative" style={{ height: 'calc(100% - 32px)' }}>
              {/* Disable interaction so the preview tile feels like a monitor,
                  not an editable surface. The link wrapper handles clicks. */}
              <div className="absolute inset-0 pointer-events-none">{t.body}</div>
            </div>
          </a>
        ))}
      </div>
      <div className="mt-4 grid gap-2 text-sm text-gray-600">
        <div>
          <span className="font-bold text-gray-800">/tv/scores</span> — match-play leaderboard, auto-scrolls flights
        </div>
        <div>
          <span className="font-bold text-gray-800">/tv/map</span> — live GPS course map
        </div>
        <div>
          <span className="font-bold text-gray-800">/tv/photos</span> — approved photos, auto-advance every 5s
        </div>
        <div>
          <span className="font-bold text-gray-800">/tv/split</span> — leaderboard + photo wall side by side
        </div>
        <div>
          <span className="font-bold text-gray-800">/tv</span> — rotates through all three every {Math.round(ROTATE_MS / 1000)}s
        </div>
      </div>
    </div>
  )
}

// GROSS / NET pill group. Saves to tournaments/{id}/tvSettings/netMode; the
// public leaderboard and every TV board read it and render net ranking/columns
// when NET. Gross is the default.
function NetPicker({ value, onChange }) {
  const opts = [
    { on: false, label: 'Gross' },
    { on: true, label: 'Net' },
  ]
  return (
    <div className="flex items-center gap-2 rounded-lg p-1"
         style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
      <span className="text-sm uppercase tracking-widest font-extrabold text-gray-500 px-1">
        TV Scoring
      </span>
      {opts.map((o) => {
        const active = value === o.on
        return (
          <button
            key={o.label}
            type="button"
            onClick={() => onChange(o.on)}
            className="rounded-md px-2.5 py-1 text-sm font-extrabold uppercase tracking-wider transition"
            style={{
              backgroundColor: active ? CLUB.primaryColor : 'transparent',
              color: active ? '#FFFFFF' : '#374151',
            }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// Slow / Medium / Fast pill group. Saves to tournaments/{id}/tvSettings/
// scrollSpeed; the live TV /tv/scores route picks it up via the existing
// tournament listener and re-tunes its scroll loop on the next render.
function SpeedPicker({ value, onChange }) {
  const opts = [
    { key: 'slow',   label: 'Slow' },
    { key: 'medium', label: 'Medium' },
    { key: 'fast',   label: 'Fast' },
  ]
  return (
    <div className="flex items-center gap-2 rounded-lg p-1"
         style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
      <span className="text-sm uppercase tracking-widest font-extrabold text-gray-500 px-1">
        TV Scroll
      </span>
      {opts.map((o) => {
        const active = value === o.key
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            className="rounded-md px-2.5 py-1 text-sm font-extrabold uppercase tracking-wider transition"
            style={{
              backgroundColor: active ? CLUB.primaryColor : 'transparent',
              color: active ? '#FFFFFF' : '#374151',
            }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
